import Keycloak from 'keycloak-js';

const KEYCLOAK_URL = process.env.REACT_APP_KEYCLOAK_URL;
const KEYCLOAK_REALM = process.env.REACT_APP_KEYCLOAK_REALM || 'livebetter';
const KEYCLOAK_CLIENT_ID = process.env.REACT_APP_KEYCLOAK_CLIENT_ID || 'livebetter-hub';

/** True when the frontend has Keycloak SSO configured. */
export const isKeycloakEnabled = Boolean(KEYCLOAK_URL);

/** Singleton Keycloak instance (null when SSO is disabled). */
const keycloak = isKeycloakEnabled
  ? new Keycloak({
      url: KEYCLOAK_URL,
      realm: KEYCLOAK_REALM,
      clientId: KEYCLOAK_CLIENT_ID,
    })
  : null;

let _initPromise = null;

/**
 * Initialise Keycloak (idempotent — safe to call multiple times).
 * Uses `check-sso` so users who already have a Keycloak session are
 * logged in silently; otherwise no redirect happens until the user
 * explicitly clicks "Sign in".
 *
 * @returns {Promise<boolean>} true if the user is authenticated.
 */
export const initKeycloak = () => {
  if (!keycloak) return Promise.resolve(false);
  if (_initPromise) return _initPromise;

  _initPromise = keycloak
    .init({
      onLoad: 'check-sso',
      silentCheckSsoRedirectUri:
        window.location.origin + '/silent-check-sso.html',
      pkceMethod: 'S256',
      checkLoginIframe: false,
    })
    .catch(() => false);

  return _initPromise;
};

/**
 * Redirect the browser to the Keycloak login page.
 * After successful login Keycloak redirects back to the given URI.
 */
export const keycloakLogin = (redirectUri) => {
  if (!keycloak) return;
  keycloak.login({ redirectUri: redirectUri || window.location.origin + '/dashboard' });
};

/**
 * Log out via Keycloak (ends both the local and Keycloak sessions).
 */
export const keycloakLogout = (redirectUri) => {
  if (!keycloak) return;
  keycloak.logout({ redirectUri: redirectUri || window.location.origin + '/login' });
};

/**
 * Open the Keycloak account console, where users manage their password and
 * sign-in methods (the Hub itself stores no credentials).
 */
export const keycloakAccountManagement = () => {
  if (!keycloak) return;
  keycloak.accountManagement();
};

/**
 * Return the current access token (refreshing it if it expires within 30s).
 * Returns null when not authenticated.
 */
export const getKeycloakToken = async () => {
  if (!keycloak?.authenticated) return null;
  try {
    await keycloak.updateToken(30);
  } catch {
    return null;
  }
  return keycloak.token;
};

export default keycloak;
